const ID = /^[0-9a-f]{32}$/;
const positiveInteger = (value) => Number.isSafeInteger(value) && value > 0;
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const exact = (value, keys) => record(value) && Object.keys(value).length === keys.length && keys.every((key) => own(value, key));
const LOCAL_WORKFLOWS = new Set(["BULK"]);
const RESERVATION_INFO_FIELDS = [
  "reservationWorkflow",
  "reservationExpectedMemberSetCount",
  "reservationBatchId",
  "reservationPermissionId",
  "reservationUnitSequence",
  "reservationMemberSetSequence",
  "reservationModels",
];

const reservationIdentity = (row) => {
  const info = row && row.info;
  return Boolean(row && row.reservationPermission) || Boolean(info && RESERVATION_INFO_FIELDS
    .some((field) => Object.prototype.hasOwnProperty.call(info, field)));
};

export const claimsReservationIdentity = reservationIdentity;

export const canDeleteBulkRows = (rows) => {
  if (!Array.isArray(rows) || rows.some(reservationIdentity)) return {
    allowed: false,
    message: "Reserved uploads cannot be deleted here. Cancel the reservation before deleting.",
    code: "RESERVATION_DELETE_BLOCKED",
  };
  return { allowed: true };
};

export const isTerminalReservationLocalRow = (row) => Boolean(
  row && !reservationIdentity(row) && row.reservationTerminal &&
  ID.test(row.reservationTerminal.reservationId || "") &&
  ["CANCELLED", "RELEASED"].includes(row.reservationTerminal.state)
);

export const reservationReadForRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) throw new Error("reservation rows are required");
  const reservationIds = new Set(rows.map((row) => row?.info?.reservationBatchId));
  if (reservationIds.size !== 1) throw new Error("reservation rows do not share one batch");
  const reservationId = Array.from(reservationIds)[0];
  if (!ID.test(reservationId || "")) throw new Error("reservation batch is invalid");
  const expectedMemberSets = rows.map((row) => ({
    unitSequence: row?.info?.reservationUnitSequence,
    memberSetSequence: row?.info?.reservationMemberSetSequence,
  }));
  if (expectedMemberSets.some(({ unitSequence, memberSetSequence }) => !positiveInteger(unitSequence) || !positiveInteger(memberSetSequence))) {
    throw new Error("reservation member set is invalid");
  }
  const keys = expectedMemberSets.map(({ unitSequence, memberSetSequence }) => `${unitSequence}:${memberSetSequence}`);
  if (new Set(keys).size !== keys.length) throw new Error("reservation member set is ambiguous");
  expectedMemberSets.sort((left, right) => left.unitSequence - right.unitSequence || left.memberSetSequence - right.memberSetSequence);
  return { reservationId, expectedMemberSets };
};

const sameValues = (left, right) => Array.isArray(left) && Array.isArray(right) &&
  left.length === right.length && left.every((value, index) => value === right[index]);

const reservationWorkflow = (reservation) => {
  if (reservation?.workflow === "regular-bulk" && reservation.billingUnitType === "bulk-image") {
    return { local: "BULK", canonical: "BULK", billingUnitType: "IMAGE" };
  }
  throw new Error("reservation workflow is invalid");
};

const canonicalUnits = (reservation, workflow) => {
  const units = reservation?.quote?.units;
  if (!Array.isArray(units) || units.length === 0) throw new Error("reservation quote identity is unavailable");
  const unitMap = new Map();
  units.forEach((unit) => {
    if (!exact(unit, ["unitSequence", "canonicalUnitIdentity"]) || !positiveInteger(unit.unitSequence) ||
      typeof unit.canonicalUnitIdentity !== "string" || unitMap.has(unit.unitSequence)) {
      throw new Error("reservation quote unit is invalid");
    }
    let identity;
    try { identity = JSON.parse(unit.canonicalUnitIdentity); } catch (error) { throw new Error("reservation quote identity is invalid"); }
    if (JSON.stringify(identity) !== unit.canonicalUnitIdentity || !record(identity) || identity.workflow !== workflow.canonical ||
      identity.billingUnitType !== workflow.billingUnitType || !Array.isArray(identity.members) || identity.members.length === 0) {
      throw new Error("reservation quote identity is invalid");
    }
    const memberSets = new Map();
    identity.members.forEach((member) => {
      const keys = ["memberSetSequence", "workSequence", "model"];
      if (!exact(member, keys) || !positiveInteger(member.memberSetSequence) || !positiveInteger(member.workSequence) ||
        typeof member.model !== "string" || member.model.length === 0) throw new Error("reservation quote member is invalid");
      if (!memberSets.has(member.memberSetSequence)) memberSets.set(member.memberSetSequence, []);
      memberSets.get(member.memberSetSequence).push(member);
    });
    const sequences = Array.from(memberSets.keys()).sort((left, right) => left - right);
    if (!sequences.every((sequence, index) => sequence === index + 1)) throw new Error("reservation quote member sets are invalid");
    memberSets.forEach((members) => {
      if (!members.every((member, index) => member.workSequence === index + 1) ||
        new Set(members.map(({ model }) => model)).size !== members.length) throw new Error("reservation quote work is invalid");
    });
    unitMap.set(unit.unitSequence, { identity, memberSets });
  });
  return unitMap;
};

const assertLocalCanonicalIdentity = ({ row, unit, workflow }) => {
  const info = row?.info;
  if (!record(info) || info.guid !== row.guid || info.reservationWorkflow !== workflow.local ||
    !positiveInteger(info.reservationExpectedMemberSetCount) || !positiveInteger(info.reservationUnitSequence) ||
    !positiveInteger(info.reservationMemberSetSequence) || !Array.isArray(info.reservationModels) || info.reservationModels.length === 0 ||
    new Set(info.reservationModels).size !== info.reservationModels.length || !own(info, "reservationPermissionId") ||
    !(info.reservationPermissionId === null || ID.test(info.reservationPermissionId || ""))) {
    throw new Error("local reservation identity is invalid");
  }
  const members = unit?.memberSets.get(info.reservationMemberSetSequence);
  if (!members || unit.memberSets.size !== info.reservationExpectedMemberSetCount ||
    !sameValues(info.reservationModels, members.map(({ model }) => model))) throw new Error("local reservation member is invalid");
  const identity = unit.identity;
  const identityMember = members[0];
  const expectedIdentity = workflow.local === "BULK" ? identity : identityMember;
  if (expectedIdentity.guid !== row.guid || expectedIdentity.laterality !== info.laterality ||
    expectedIdentity.s3Bucket !== info.s3Bucket || expectedIdentity.s3Key !== info.s3Key) {
    throw new Error("local reservation object identity does not match quote");
  }
  if (workflow.local === "BULK" && (!exact(identity, ["workflow", "billingUnitType", "guid", "laterality", "s3Bucket", "s3Key", "members"]) ||
    info.reservationMemberSetSequence !== 1 || unit.memberSets.size !== 1)) throw new Error("regular reservation identity is invalid");
};

export const reconcileTerminalReservationRows = ({ rows, reservation, permissionSnapshot } = {}) => {
  if (!Array.isArray(rows) || !reservation || !permissionSnapshot) throw new Error("reservation reconciliation is unavailable");
  const reservationId = reservation.reservationId;
  const terminalState = String(reservation.state || "").toUpperCase();
  if (!ID.test(reservationId || "") || !["CANCELLED", "RELEASED"].includes(terminalState) ||
    permissionSnapshot.reservationId !== reservationId || permissionSnapshot.batchState !== terminalState ||
    permissionSnapshot.uploadEligible !== false || !Array.isArray(permissionSnapshot.permissions)) {
    throw new Error("reservation is not safely terminal");
  }
  const targetRows = rows.filter((row) => row?.info?.reservationBatchId === reservationId);
  const { expectedMemberSets } = reservationReadForRows(targetRows);
  const workflow = reservationWorkflow(reservation);
  const unitMap = canonicalUnits(reservation, workflow);
  const quoteMemberKeys = [];
  unitMap.forEach(({ memberSets }, unitSequence) => memberSets.forEach((_members, memberSetSequence) => {
    quoteMemberKeys.push(`${unitSequence}:${memberSetSequence}`);
  }));
  const localMemberKeys = expectedMemberSets.map(({ unitSequence, memberSetSequence }) => `${unitSequence}:${memberSetSequence}`);
  if (quoteMemberKeys.length !== localMemberKeys.length || quoteMemberKeys.some((key) => !localMemberKeys.includes(key))) {
    throw new Error("local reservation batch does not match quote");
  }
  targetRows.forEach((row) => assertLocalCanonicalIdentity({ row, unit: unitMap.get(row.info.reservationUnitSequence), workflow }));
  const permissionByKey = new Map(permissionSnapshot.permissions.map((permission) => [
    `${permission.unitSequence}:${permission.memberSetSequence}`,
    permission,
  ]));
  if (permissionByKey.size !== permissionSnapshot.permissions.length || permissionByKey.size !== expectedMemberSets.length) {
    throw new Error("reservation permission coverage is incomplete");
  }
  targetRows.forEach((row) => {
    const info = row.info;
    const permission = permissionByKey.get(`${info.reservationUnitSequence}:${info.reservationMemberSetSequence}`);
    const expectedPermissionId = info.reservationPermissionId;
    if (!permission || !exact(permission, ["unitSequence", "memberSetSequence", "memberSetState", "permissionId", "permissionExpiresAt", "permissionRevokedAt"])) {
      throw new Error("reservation permission state is incomplete");
    }
    const revoked = expectedPermissionId && permission?.memberSetState === "REVOKED" &&
      permission.permissionId === expectedPermissionId && typeof permission.permissionRevokedAt === "string";
    const neverIssued = expectedPermissionId === null && permission?.memberSetState === "RESERVED" &&
      permission.permissionId === null && permission.permissionExpiresAt === null && permission.permissionRevokedAt === null;
    if (!revoked && !neverIssued) throw new Error("reservation permission is not safely terminal");
  });
  return rows.map((row) => {
    if (row?.info?.reservationBatchId !== reservationId) return row;
    const info = { ...row.info };
    RESERVATION_INFO_FIELDS.forEach((field) => delete info[field]);
    return {
      ...row,
      info,
      reservationPermission: undefined,
      reservationTerminal: { reservationId, state: terminalState },
      status: terminalState === "CANCELLED" ? "Reservation cancelled" : "Reservation released",
      uploadErrorCode: undefined,
    };
  });
};

export const protectLegacyReservationRow = (row) => {
  if (!row || !["Saved", "Uploading"].includes(row.status) || reservationIdentity(row)) return row;
  return {
    ...row,
    status: "Saved",
    uploadErrorCode: "LEGACY_RESERVATION_REQUIRED",
  };
};

export const isReservationUploadProgressPersisted = (result) => result === 1;

export const protectUnknownReservationUpload = ({ row, error } = {}) => {
  if (!row || error?.code !== "UPLOAD_OUTCOME_UNKNOWN") return null;
  return {
    ...row,
    status: "Uploading",
    uploadErrorCode: error.code,
  };
};

export const applyReservationPermissionsToRows = ({ rows, admission } = {}) => {
  const reservation = admission && admission.reservation;
  const reservationId = reservation && reservation.reservationId;
  const apiWorkflow = reservation && reservation.workflow;
  const billingUnitType = reservation && reservation.billingUnitType;
  const localWorkflow = apiWorkflow === "regular-bulk" && billingUnitType === "bulk-image" ? "BULK" : null;
  if (!Array.isArray(rows) || rows.length < 1 || !ID.test(reservationId || "") || !LOCAL_WORKFLOWS.has(localWorkflow) || !Array.isArray(admission.units)) throw new Error("invalid reservation row mapping");

  const permissions = admission.units.flatMap((unit) => {
    if (!positiveInteger(unit && unit.unitSequence) || !Array.isArray(unit.memberSets) || !unit.planUnit || !Array.isArray(unit.planUnit.members)) throw new Error("invalid reservation unit mapping");
    return unit.memberSets.map((memberSet) => {
      const permission = memberSet && memberSet.permission;
      if (!positiveInteger(memberSet && memberSet.memberSetSequence) || !permission ||
        permission.unitSequence !== unit.unitSequence || permission.memberSetSequence !== memberSet.memberSetSequence) {
        throw new Error("invalid reservation unit mapping");
      }
      return permission;
    });
  });
  if (permissions.length !== rows.length) throw new Error("reservation permission coverage mismatch");
  const compositeKeys = new Set();
  const identityKeys = new Set();
  permissions.forEach((permission) => {
    if (!permission || permission.reservationId !== reservationId || !ID.test(permission.permissionId || "") || !ID.test(permission.objectWriteNonce || "") ||
      !positiveInteger(permission.unitSequence) || !positiveInteger(permission.memberSetSequence) || !["left", "right"].includes(permission.laterality) ||
      typeof permission.guid !== "string" || !permission.guid || typeof permission.s3Bucket !== "string" || !permission.s3Bucket || typeof permission.s3Key !== "string" || !permission.s3Key ||
      typeof permission.permissionExpiresAt !== "string" || permission.permissionRevokedAt !== null) throw new Error("invalid reservation permission");
    const composite = `${permission.unitSequence}:${permission.memberSetSequence}`;
    const identity = `${permission.guid}:${permission.laterality}`;
    if (compositeKeys.has(composite) || identityKeys.has(identity)) throw new Error("duplicate reservation permission");
    compositeKeys.add(composite);
    identityKeys.add(identity);
  });

  const permissionByIdentity = new Map(permissions.map((permission) => [`${permission.guid}:${permission.laterality}`, permission]));
  const modelsByMemberSet = new Map();
  admission.units.forEach((unit) => {
    unit.memberSets.forEach((memberSet) => {
      const models = unit.planUnit.members
        .filter((member) => member.memberSetSequence === memberSet.memberSetSequence)
        .sort((left, right) => left.workSequence - right.workSequence)
        .map((member) => member.model);
      if (!models.length || models.some((model) => typeof model !== "string" || !model) || new Set(models).size !== models.length) {
        throw new Error("invalid reservation model mapping");
      }
      modelsByMemberSet.set(`${unit.unitSequence}:${memberSet.memberSetSequence}`, models);
    });
  });
  return rows.map((row) => {
    const info = row && row.info;
    const permission = info && permissionByIdentity.get(`${row.guid}:${info.laterality}`);
    if (!permission || info.guid !== row.guid) throw new Error("reservation row identity mismatch");
    permissionByIdentity.delete(`${row.guid}:${info.laterality}`);
    return {
      ...row,
      reservationPermission: permission,
      info: {
        ...info,
        reservationBatchId: reservationId,
        reservationWorkflow: localWorkflow,
        reservationExpectedMemberSetCount: admission.units.find(({ unitSequence }) => unitSequence === permission.unitSequence).memberSets.length,
        reservationPermissionId: permission.permissionId,
        reservationUnitSequence: permission.unitSequence,
        reservationMemberSetSequence: permission.memberSetSequence,
        reservationModels: modelsByMemberSet.get(`${permission.unitSequence}:${permission.memberSetSequence}`),
        s3Bucket: permission.s3Bucket,
        s3Key: permission.s3Key,
      },
    };
  });
};

export const selectCompleteReservationUploadRows = ({ rows, uploadedRows } = {}) => {
  if (!Array.isArray(rows) || !Array.isArray(uploadedRows)) throw new Error("invalid reservation upload rows");
  const identityFor = (row) => {
    const info = row && row.info;
    const batchId = info && info.reservationBatchId;
    const unitSequence = info && info.reservationUnitSequence;
    const memberSetSequence = info && info.reservationMemberSetSequence;
    const permissionId = info && info.reservationPermissionId;
    const workflow = info && info.reservationWorkflow;
    const expectedMemberSetCount = info && info.reservationExpectedMemberSetCount;
    const requiredMemberSetCount = 1;
    if (!ID.test(batchId || "") || !ID.test(permissionId || "") || !positiveInteger(unitSequence) || !positiveInteger(memberSetSequence) ||
      !LOCAL_WORKFLOWS.has(workflow) || expectedMemberSetCount !== requiredMemberSetCount ||
      typeof row.guid !== "string" || !row.guid || !["left", "right"].includes(info.laterality)) throw new Error("invalid reservation upload row");
    return {
      unitKey: `${batchId}:${unitSequence}`,
      memberKey: `${batchId}:${unitSequence}:${memberSetSequence}:${row.guid}:${info.laterality}:${permissionId}`,
      workflow,
      expectedMemberSetCount,
    };
  };
  const expectedByUnit = new Map();
  rows.forEach((row) => {
    const { unitKey, memberKey, workflow, expectedMemberSetCount } = identityFor(row);
    if (!expectedByUnit.has(unitKey)) expectedByUnit.set(unitKey, { memberKeys: new Set(), workflow, expectedMemberSetCount });
    const expected = expectedByUnit.get(unitKey);
    if (expected.workflow !== workflow || expected.expectedMemberSetCount !== expectedMemberSetCount || expected.memberKeys.has(memberKey)) throw new Error("invalid reservation upload row");
    expected.memberKeys.add(memberKey);
  });
  const uploadedByMember = new Map();
  uploadedRows.forEach((row) => {
    const { memberKey } = identityFor(row);
    if (uploadedByMember.has(memberKey)) throw new Error("duplicate uploaded reservation row");
    uploadedByMember.set(memberKey, row);
  });
  const completeUnits = new Set();
  expectedByUnit.forEach(({ memberKeys, expectedMemberSetCount }, key) => {
    if (memberKeys.size === expectedMemberSetCount && Array.from(memberKeys).every((memberKey) => uploadedByMember.has(memberKey))) completeUnits.add(key);
  });
  return uploadedRows.filter((row) => completeUnits.has(identityFor(row).unitKey));
};
